"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import { useState, type ReactNode } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Bot,
  Check,
  CircleDollarSign,
  FileLock2,
  Layers3,
  Network,
  Sparkles,
} from "lucide-react";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import { PageCta, PageHero } from "@/components/platform/v2/PageEdge";
import { Button } from "@/components/platform/v2/ui/button";
import InvestorContactDialog, {
  type OfferType,
} from "@/components/platform/v2/InvestorContactDialog";
import { PROOF } from "@/data/platform";
import { INTEGRATIONS } from "@/data/integrations";

const channels = [
  "Сайт",
  "Приложение",
  "Офлайн",
  "Мессенджеры",
  "Внешние сервисы",
];
const contour = [
  "Клиент",
  "CRM",
  "Продажи",
  "Коммуникации",
  "Заказы",
  "Лояльность",
  "Автоматизация",
  "AI",
  "Аналитика",
];
const liveCapabilities = [
  "CRM и единый профиль",
  "Заявки, сделки и задачи",
  "Каталоги и заказы",
  "Квизы и формы",
  "Лояльность и рефералы",
  "Рассылки и уведомления",
  "Роли и доступы",
  "Сайты и приложение",
];
const problems = [
  "Заявки расходятся по каналам",
  "CRM становится складом контактов",
  "Сайт существует отдельно от продаж",
  "Коммуникации теряются",
  "Клиентская база редко возвращается",
  "Каждый сервис добавляет новый кабинет",
];
const audiences = [
  "Услуги",
  "Торговля",
  "B2B",
  "Образование",
  "Мероприятия",
  "Сообщества",
  "Франшизы и сети",
  "Фонды и НКО",
];
const revenue = [
  "Доступ к платформе",
  "AI-потребление",
  "Дополнительные расширения",
  "Сайты",
  "Внедрение",
  "Дизайн",
  "Интеграции",
  "Отраслевые решения",
];
const scale = [
  ["01", "Доступ к платформе", "Регулярная модель выручки."],
  ["02", "Партнёры", "Продажи и внедрения через партнёрскую сеть."],
  ["03", "Рефералы", "Действующие клиенты становятся каналом распространения."],
  ["04", "Бизнес-сценарии", "Одна база адаптируется к разным моделям работы."],
  ["05", "Интеграции", "Не нужно заменять всю инфраструктуру компании."],
  ["06", "AI", "Снижает объём ручной работы внутри процессов."],
] as const;
const ecosystem = [
  ["VCENTRE.ONE", "Клиенты, продажи, коммуникации и цифровые каналы", true],
  [
    "VCENTRE.FINANCE",
    "Финансы, аналитика и помощь в управленческих решениях",
    false,
  ],
  ["VCENTRE.MARKETING", "Продвижение и маркетинговые каналы", false],
  ["VCENTRE.CONTENT", "Создание и управление контентом", false],
  ["VCENTRE.JURIST", "Документы, договоры и юридический контур", false],
  ["VCENTRE.PURCHASE", "Закупки и работа с поставщиками", false],
] as const;
const capital = [
  "Продажи и партнёрская сеть",
  "Продукт и автоматизация",
  "Интеграции",
  "Маркетинг и упаковка",
  "Внедрение и поддержка",
  "Инфраструктура и безопасность",
  "Юридический контур",
  "Россия → СНГ",
];
const value = [
  [
    "Регулярная выручка",
    "Доступ к платформе формирует повторяемую основу модели.",
  ],
  [
    "Рост клиентской базы",
    "Каждый новый платящий бизнес расширяет масштаб платформы.",
  ],
  ["Апсейл", "AI, сайты, интеграции и расширения развивают экономику клиента."],
  [
    "Партнёрская сеть",
    "Продажи могут расти не только силами собственной команды.",
  ],
  [
    "Экосистемный потенциал",
    "VCENTRE.ONE может стать входной точкой в другие продукты.",
  ],
  [
    "Стратегическая ценность",
    "Возможны синергии с банками, телекомом, интеграторами и B2B-экосистемами.",
  ],
] as const;
const materials = [
  "Инвестиционный меморандум",
  "Бизнес-план",
  "Финансовая модель",
  "Roadmap",
  "Данные о продукте",
  "Структура сделки для обсуждения",
];

const Card = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => <article className={`surface ${className}`}>{children}</article>;

const InvestorsPageComponent = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [offer, setOffer] = useState<OfferType>("financial");
  const [wantMaterials, setWantMaterials] = useState(false);

  const openForm = (nextOffer: OfferType, materialsRequested = false) => {
    setOffer(nextOffer);
    setWantMaterials(materialsRequested);
    setDialogOpen(true);
  };

  return (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
      <PageHero
        eyebrow="Инвесторам"
        title={
          <>
            Продукт уже создан.
            <br />
            <span className="text-primary">Следующий этап — масштаб.</span>
          </>
        }
        text={
          <p>
            VCENTRE.ONE объединяет клиентов, продажи, коммуникации, приложение,
            сайт, автоматизацию и AI. Платформа уже проверяется в реальных
            проектах; следующая задача — масштабировать продажи, партнёрскую
            сеть и присутствие на рынке.
          </p>
        }
        actions={
          <>
            <Button
              size="lg"
              variant="site"
              onClick={() => openForm("financial")}
            >
              Обсудить инвестиции
            </Button>
            <Button
              size="lg"
              variant="siteOutline"
              onClick={() => openForm("financial", true)}
            >
              Получить материалы
            </Button>
          </>
        }
        note={<p>Условия инвестиционной сделки обсуждаются индивидуально.</p>}
        visual={
          <img
            src={"/assets/images/platform/investors-mascot-hero.png"}
            alt="Маскот VCENTRE.IT показывает рост продукта от запуска до масштабирования"
            width={756}
            height={758}
            loading="eager"
            className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
          />
        }
      />

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Актив"
          title={
            <>
              Не ещё одна CRM.
              <br />
              Не ещё один конструктор.
            </>
          }
          text="Компания получает внутреннюю рабочую систему для команды и собственный цифровой контур для клиента. Независимо от точки входа, дальше бизнес работает с клиентом в одной системе."
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-[1fr_auto_1.25fr] lg:items-center">
          <Card>
            <p className="eyebrow mb-5">Каналы</p>
            <div className="flex flex-wrap gap-2">
              {channels.map((x) => (
                <span key={x} className="pill pill-neutral">
                  {x}
                </span>
              ))}
            </div>
          </Card>
          <ArrowRight className="mx-auto hidden text-primary lg:block" />
          <ArrowDown className="mx-auto text-primary lg:hidden" />
          <Card className="border-primary/40 bg-primary/[0.06]">
            <h3 className="display-3 text-primary">VCENTRE.ONE</h3>
            <div className="mt-5 flex flex-wrap gap-2">
              {contour.map((x) => (
                <span key={x} className="pill pill-neutral">
                  {x}
                </span>
              ))}
            </div>
          </Card>
        </div>
        <p className="mx-auto mt-8 max-w-3xl text-center text-lg font-bold text-foreground">
          VCENTRE.ONE соединяет внешнюю жизнь клиента и внутреннюю работу
          компании.
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Не гипотеза"
          title="Инвестируем не в идею с нуля."
          text="Платформа создана и развивается на реальных бизнес-задачах. Мы не публикуем неподтверждённые масштабы — показываем то, что уже работает в продукте."
        />
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {liveCapabilities.map((x) => (
            <Card key={x} className="flex items-start gap-3 p-5">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <p className="font-semibold">{x}</p>
            </Card>
          ))}
        </div>
        <p className="mx-auto mt-10 max-w-4xl text-center display-3">
          Продукту не нужно доказывать, что он может существовать.{" "}
          <span className="text-primary">
            Теперь нужно доказать, насколько большим он может стать.
          </span>
        </p>
      </Section>

      <Section tone="quiet">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <p className="eyebrow">Происхождение продукта</p>
            <h2 className="display-2 mt-4">
              Мы строили то, чего не хватало нам самим.
            </h2>
            <p className="lede mt-5">
              VCENTRE.ONE вырос из предпринимательского опыта в B2B, продажах,
              работе с клиентами, поставщиками, ВЭД, мероприятиях и операционных
              процессах.
            </p>
            <p className="mt-6 text-lg font-bold text-primary">
              Мы не пытаемся угадать, как работает бизнес. Мы сами из него
              вышли.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {problems.map((x) => (
              <Card key={x} className="p-5">
                <span className="mb-3 block h-px w-8 bg-primary" />
                <p className="font-semibold">{x}</p>
              </Card>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Рынок"
          title="Миллионы компаний решают одну и ту же задачу."
          text="Первый рынок VCENTRE.ONE — малый и средний бизнес. Проблема не в отсутствии CRM, сайтов, банков, учётных систем и мессенджеров, а в том, что они редко работают как единая система."
        />
        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
          {audiences.map((x) => (
            <Card key={x} className="p-5 text-center font-bold">
              {x}
            </Card>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-3xl text-center body-text">
          Платформа масштабируется не вокруг одной отрасли, а вокруг базовой
          задачи: связать клиента, продажу, коммуникацию и действие. Следующий
          логичный рынок — СНГ и близкие русскоязычные рынки.
        </p>
      </Section>

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Бизнес-модель"
          title="Регулярная выручка — основа. Дополнительные продукты — рост чека."
        />
        <div className="mt-10 grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
          <Card className="border-primary/40 bg-primary/[0.06]">
            <p className="eyebrow mb-3">Основа</p>
            <IconHeading
              as="h3"
              icon={<CircleDollarSign className="h-7 w-7 text-primary" />}
              className="display-3"
            >
              Регулярная выручка от доступа к VCENTRE.ONE
            </IconHeading>
            <p className="body-text mt-4">
              Повторяемая модель вместо зависимости только от разовых проектов.
            </p>
          </Card>
          <Card>
            <p className="eyebrow mb-5">Дополнительно</p>
            <div className="grid grid-cols-2 gap-3">
              {revenue.slice(1).map((x) => (
                <div
                  key={x}
                  className="rounded-lg border border-border/60 bg-[hsl(var(--deep-black))] p-3 text-sm font-semibold"
                >
                  {x}
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div className="mt-8 flex flex-col items-center gap-3 lg:flex-row lg:justify-center">
          {[
            "Клиент",
            "Доступ к платформе",
            "Расширения",
            "AI",
            "Интеграции",
            "Развитие проекта",
          ].map((x, i) => (
            <div key={x} className="contents">
              <span
                className={
                  i === 1
                    ? "pill border-primary/40 bg-primary/10 text-primary"
                    : "pill pill-neutral"
                }
              >
                {x}
              </span>
              {i < 5 && (
                <ArrowRight className="hidden h-4 w-4 text-primary lg:block" />
              )}
            </div>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-3xl text-center body-text">
          Один подключённый бизнес может создавать длительные отношения и
          повторяемую выручку. Конкретный LTV публично не заявляется без
          подтверждённых данных.
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Масштабирование"
          title="Рост не должен требовать пропорционального роста команды."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {scale.map(([n, title, text]) => (
            <Card key={n}>
              <span className="text-sm font-bold text-primary">{n}</span>
              <h3 className="display-3 mt-4">{title}</h3>
              <p className="body-text mt-3">{text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Интеграционная стратегия"
          title="Не воюем с 1С, CRM и банками."
          text="VCENTRE.ONE создаёт клиентский и операционный слой рядом с существующей инфраструктурой. Мы показываем текущие статусы честно."
        />
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {INTEGRATIONS.map((x) => (
            <Card
              key={x.id}
              className="flex flex-col items-center p-5 text-center"
            >
              <h3 className="font-bold">{x.name}</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                {x.description}
              </p>
            </Card>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-3xl text-center text-lg font-bold">
          Мы можем расти рядом с существующим IT-рынком,{" "}
          <span className="text-primary">
            а не только за счёт его замещения.
          </span>
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Экосистема"
          title="Шесть направлений — один бизнес"
          text="Каждый продукт решает свою задачу. Максимальная ценность появляется, когда они начинают понимать один и тот же бизнес."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ecosystem.map(([name, text, accent]) => (
            <Card
              key={name}
              className={
                accent
                  ? "flex flex-col items-center gap-3 border-primary/40 bg-primary/[0.07] text-center"
                  : "surface-hover flex flex-col items-center gap-3 text-center"
              }
            >
              <h3
                className={
                  accent
                    ? "text-lg font-extrabold text-primary sm:text-xl"
                    : "text-lg font-bold text-foreground sm:text-xl"
                }
              >
                {name}
              </h3>
              <p className="body-text">{text}</p>
            </Card>
          ))}
        </div>
        <Card className="mx-auto mt-8 flex max-w-4xl flex-col items-center text-center">
          <IconHeading
            as="h3"
            icon={<Bot className="h-7 w-7 text-primary" />}
            className="display-3"
          >
            AI, который знает не запрос. А компанию.
          </IconHeading>
          <p className="body-text mt-4">
            AI-помощь с контентом и клиентский помощник входят во все платные
            тарифы. GENIUS развивает интеллектуальный слой над CRM, данными и
            процессами: анализ бизнеса, поиск проблем, управленческие рекомендации
            и автоматизацию доступных действий.
          </p>
        </Card>
      </Section>

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Почему сейчас"
          title="Продукт уже есть. Окно масштабирования открыто сейчас."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Layers3, "Продукт", "Базовая платформа создана."],
            [
              BarChart3,
              "Рынок",
              "Бизнес уже платит за CRM, сайты, автоматизацию и AI.",
            ],
            [
              Sparkles,
              "AI",
              "Можно переходить от учёта к интеллектуальной помощи.",
            ],
            [
              Network,
              "Экосистема",
              "Формируются взаимосвязанные продукты вокруг одного бизнеса.",
            ],
          ].map(([Icon, title, text]) => {
            const IconComponent = Icon as typeof Layers3;
            return (
              <Card key={title as string}>
                <IconHeading
                  as="h3"
                  icon={<IconComponent className="h-6 w-6 text-primary" />}
                  className="font-bold"
                >
                  {title as string}
                </IconHeading>
                <p className="body-text mt-2">{text as string}</p>
              </Card>
            );
          })}
        </div>
        <div className="mt-16 grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="eyebrow">Использование капитала</p>
            <h2 className="display-2 mt-4">
              Не на создание идеи. На ускорение.
            </h2>
            <p className="lede mt-5">
              Конкретные суммы и бюджет раскрываются в закрытых материалах после
              знакомства.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {capital.map((x, i) => (
              <div
                key={x}
                className="flex gap-3 border-b border-border/60 py-3"
              >
                <span className="text-xs font-bold text-primary">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-semibold">{x}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Инвестиционная ценность"
          title="Инвестор входит не только в текущий продукт."
          text="Это этап перехода от работающей платформы к масштабируемому бизнесу. Мы не обещаем конкретный финансовый результат — показываем механизмы, которые могут формировать ценность актива."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {value.map(([title, text], i) => (
            <Card key={title}>
              <span className="text-xs font-bold text-primary">0{i + 1}</span>
              <h3 className="mt-4 text-lg font-bold">{title}</h3>
              <p className="body-text mt-3">{text}</p>
            </Card>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border border-primary/40 bg-primary/[0.06] p-6 text-center sm:p-8">
          <p className="mx-auto max-w-4xl text-base font-semibold leading-relaxed sm:text-lg">
            Стоимость актива может меняться вместе с клиентской базой,
            регулярной выручкой, технологиями, партнёрской сетью и экосистемой.
            Это потенциал, а не обещание доходности.
          </p>
        </div>
      </Section>

      <Section tone="quiet">
        <SectionHeading align="left" title="Подтверждённые факты" />
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PROOF.map((p) => (
            <a
              key={p.title}
              href={p.href}
              target="_blank"
              rel="noreferrer"
              className="surface surface-hover flex items-start justify-between gap-4"
            >
              <div>
                <p className="text-base font-bold text-foreground">{p.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{p.org}</p>
              </div>
              <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-primary" />
            </a>
          ))}
        </div>
      </Section>

      <Section tone="quiet">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <p className="eyebrow">Закрытые материалы</p>
            <h2 className="display-2 mt-4">Хотите увидеть цифры?</h2>
            <p className="lede mt-5">
              После первичного знакомства предоставим материалы для предметного
              обсуждения. При необходимости — после проверки контакта и NDA.
            </p>
            <div className="mt-7 grid gap-2 sm:grid-cols-2">
              {materials.map((x) => (
                <div
                  key={x}
                  className="flex gap-2 text-sm text-muted-foreground"
                >
                  <FileLock2 className="h-4 w-4 shrink-0 text-primary" />
                  {x}
                </div>
              ))}
            </div>
            <Button
              variant="site"
              className="mt-8"
              onClick={() => openForm("financial", true)}
            >
              Запросить инвестиционные материалы
            </Button>
          </div>
          <div className="rounded-2xl border border-primary/30 bg-[hsl(var(--deep-black))] p-8">
            <FileLock2 className="h-9 w-9 text-primary" />
            <p className="eyebrow mt-10">Investment materials</p>
            <p className="mt-3 text-2xl font-extrabold text-primary">
              CONFIDENTIAL
            </p>
            <div className="mt-8 space-y-3">
              {["w-4/5", "w-3/5", "w-2/3", "w-1/2"].map((w) => (
                <div key={w} className={`h-2 rounded bg-border/70 ${w}`} />
              ))}
            </div>
            <p className="body-text mt-8">Не размещаются в открытом доступе.</p>
          </div>
        </div>
      </Section>

      <Section>
        <PageCta
          eyebrow="Закрытый разговор"
          title="Мы открыты к разговору."
          text={
            <p>
              Если вы видите потенциал VCENTRE.ONE и можете помочь компании
              расти быстрее — финансово или стратегически — давайте обсуждать.
            </p>
          }
          actions={
            <>
              <Button
                size="lg"
                variant="site"
                onClick={() => openForm("financial")}
              >
                Начать разговор
              </Button>
              <Button
                size="lg"
                variant="siteOutline"
                onClick={() => openForm("financial", true)}
              >
                Получить закрытые материалы
              </Button>
            </>
          }
          note={
            <p>
              Информация на странице не является публичной офертой или обещанием
              инвестиционной доходности.
            </p>
          }
          visual={
            <img
              src={"/assets/images/platform/investors-mascot-hero.png"}
              alt="Маскот VCENTRE.IT приглашает обсудить инвестиции и партнёрство"
              width={756}
              height={760}
              loading="lazy"
              className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
            />
          }
        />
      </Section>
      <InvestorContactDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultOffer={offer}
        defaultWantMaterials={wantMaterials}
      />
    </div>
  );
};

export default InvestorsPageComponent;
